import React, { useMemo } from 'react';
import { Button, CircularProgress, Link, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate, useParams } from 'react-router-dom';
import { marketingPersonApi } from '../../../modules/services/marketingPersonService';
import { downloadPayoutExcel } from '../../../common/downloadMarketingPersonsExcel';

const PayoutDetailsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { payoutId } = useParams<{ payoutId: string }>();
  const id = Number(payoutId);

  const { data, isFetching } = marketingPersonApi.useGetPayoutQuery({ payoutId: id }, { skip: !id });

  const events = useMemo(() => (data?.items || []).filter(i => i.kind === 'event'), [data]);
  const referrals = useMemo(() => (data?.items || []).filter(i => i.kind === 'referral'), [data]);

  const eventColumns: GridColDef[] = [
    { field: 'eventId', headerName: 'ID', flex: 0.4, minWidth: 60 },
    { field: 'eventName', headerName: 'Event', flex: 1.4, minWidth: 160 },
    { field: 'organizerName', headerName: 'Organizer', flex: 1.2, minWidth: 140 },
    {
      field: 'eventEndDate',
      headerName: 'End date',
      flex: 0.8, minWidth: 110,
      renderCell: (p: any) => <span>{p.row.eventEndDate ? new Date(p.row.eventEndDate).toLocaleDateString() : '-'}</span>,
    },
    { field: 'ticketsSold', headerName: 'Tickets', flex: 0.5, minWidth: 70 },
    {
      field: 'totalSales', headerName: 'Sales', flex: 0.8, minWidth: 100,
      renderCell: (p: any) => <span>₪{Number(p.row.totalSales || 0).toLocaleString()}</span>,
    },
    {
      field: 'totalFees', headerName: 'Fees', flex: 0.8, minWidth: 100,
      renderCell: (p: any) => <span>₪{Number(p.row.totalFees || 0).toLocaleString()}</span>,
    },
    {
      field: 'amount', headerName: 'Commission', flex: 0.9, minWidth: 110,
      renderCell: (p: any) => <Typography variant="body2" color="success.main" fontWeight="bold">₪{Number(p.row.amount || 0).toLocaleString()}</Typography>,
    },
  ];

  const referralColumns: GridColDef[] = [
    { field: 'organizerId', headerName: 'ID', flex: 0.4, minWidth: 60 },
    { field: 'organizerName', headerName: 'Organizer', flex: 1.4, minWidth: 160 },
    { field: 'organizerPhone', headerName: 'Phone', flex: 1, minWidth: 120 },
    {
      field: 'totalSales', headerName: 'Sales (all time)', flex: 1, minWidth: 130,
      renderCell: (p: any) => <span>₪{Number(p.row.totalSales || 0).toLocaleString()}</span>,
    },
    {
      field: 'totalFees', headerName: 'Fees (all time)', flex: 1, minWidth: 130,
      renderCell: (p: any) => <span>₪{Number(p.row.totalFees || 0).toLocaleString()}</span>,
    },
    {
      field: 'salesLast60Days', headerName: 'Sales (60d)', flex: 1, minWidth: 120,
      renderCell: (p: any) => <span>₪{Number(p.row.salesLast60Days || 0).toLocaleString()}</span>,
    },
    {
      field: 'feesLast60Days', headerName: 'Fees (60d)', flex: 1, minWidth: 120,
      renderCell: (p: any) => <span>₪{Number(p.row.feesLast60Days || 0).toLocaleString()}</span>,
    },
    {
      field: 'amount', headerName: 'Referral', flex: 0.9, minWidth: 110,
      renderCell: (p: any) => <Typography variant="body2" color="success.main" fontWeight="bold">₪{Number(p.row.amount || 0).toLocaleString()}</Typography>,
    },
  ];

  if (isFetching || !data) return <CircularProgress color="warning" />;

  return (
    <div style={{ padding: '0 10px', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, height: 70 }}>
        <Link color="warning.main" sx={{ cursor: 'pointer', fontSize: 20 }} onClick={() => navigate('/marketing-persons/payouts')}>
          ← Back to Payouts
        </Link>
        <Typography fontSize={20} color="warning.main" fontWeight="bold">
          Payout #{data.id} — {data.marketingPersonName || data.marketingPersonPhone}
        </Typography>
        <Button
          size="small"
          color="warning"
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => downloadPayoutExcel(data.id)}
        >
          Download Excel
        </Button>
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 24, padding: 16, border: '1px solid #fc7f03', borderRadius: 8 }}>
        <Stat label="Period" value={`${data.dateFrom ? new Date(data.dateFrom).toLocaleDateString() : '-'} → ${data.dateTo ? new Date(data.dateTo).toLocaleDateString() : '-'}`} />
        <Stat label="Status" value={data.status} />
        <Stat label="Events" value={String(data.eventItemsCount)} />
        <Stat label="Referrals" value={String(data.referralItemsCount)} />
        <Stat label="Tickets" value={String(data.totalTickets)} />
        <Stat label="Sales" value={`₪${Number(data.totalSales || 0).toLocaleString()}`} />
        <Stat label="Handling fees" value={`₪${Number(data.totalFees || 0).toLocaleString()}`} />
        <Stat label="Event commission" value={`₪${Number(data.totalEventCommission || 0).toLocaleString()}`} />
        <Stat label="Referral commission" value={`₪${Number(data.totalReferralCommission || 0).toLocaleString()}`} />
        <Stat label="TOTAL PAYOUT" value={`₪${Number(data.totalAmount || 0).toLocaleString()}`} strong />
        <Stat label="Created by" value={data.createdByPhone || data.createdByName || '-'} />
        <Stat label="Created at" value={data.createDate ? new Date(data.createDate).toLocaleString() : '-'} />
      </div>

      <Typography variant="h6" color="warning.main" sx={{ mb: 1 }}>Events</Typography>
      <DataGrid
        sx={{
          backgroundColor: '#ede9e4',
          border: 'none',
          marginBottom: 3,
          '.MuiDataGrid-columnHeaders': { backgroundColor: 'warning.main', color: '#fff' },
          '.MuiDataGrid-cell': { border: '1px solid #fc7f03' },
        }}
        rows={events.map(e => ({ ...e }))}
        columns={eventColumns}
        getRowHeight={() => 'auto'}
        rowsPerPageOptions={[10, 25, 50]}
        autoHeight
      />

      <Typography variant="h6" color="warning.main" sx={{ mb: 1 }}>Referral Commissions</Typography>
      <DataGrid
        sx={{
          backgroundColor: '#ede9e4',
          border: 'none',
          marginBottom: 3,
          '.MuiDataGrid-columnHeaders': { backgroundColor: 'warning.main', color: '#fff' },
          '.MuiDataGrid-cell': { border: '1px solid #fc7f03' },
        }}
        rows={referrals.map(r => ({ ...r }))}
        columns={referralColumns}
        getRowHeight={() => 'auto'}
        rowsPerPageOptions={[10, 25, 50]}
        autoHeight
      />

      {data.note && (
        <div style={{ padding: 16, border: '1px solid #fc7f03', borderRadius: 8, marginBottom: 24 }}>
          <Typography variant="body2" color="textSecondary">Note</Typography>
          <Typography>{data.note}</Typography>
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <div style={{ padding: '12px 20px', borderRadius: 8, backgroundColor: '#ede9e4', textAlign: 'center' }}>
    <Typography variant="body2" color="textSecondary">{label}</Typography>
    <Typography variant={strong ? 'h6' : 'body1'} color={strong ? 'success.main' : 'warning.main'} fontWeight={strong ? 'bold' : 500}>{value}</Typography>
  </div>
);

export default PayoutDetailsScreen;
